import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Save, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { DAYS_OF_WEEK, INSTRUMENTS } from "../lib/types";
import { STUDENT_TYPES, STUDENT_TYPE_STYLES } from "../lib/studentTypes";

/**
 * Reusable form for creating OR editing a student. Pure presentational +
 * local form state. The parent supplies an optional `initial` student and a
 * single `onSubmit(payload)` handler.
 *
 * Notes / non-obvious decisions:
 *   • `availableDays` is filtered by the studio's working week from Settings,
 *     so the parent never has to pass DAYS_OF_WEEK directly.
 *   • In edit mode we keep any existing day even if it falls outside the
 *     current working-week setting, so an admin can toggle it off without
 *     losing data. (We mark such days with the chip but don't auto-strip.)
 */
export default function StudentForm({
    initial = null,
    availableDays,
    submitLabel = "Save Student",
    submitDisabled = false,
    cancelTo = "/students",
    onSubmit,
    testIdPrefix = "",
}) {
    const [name, setName] = useState(initial?.name || "");
    const [instrument, setInstrument] = useState(initial?.instrument || "");
    const [customInstrument, setCustomInstrument] = useState("");
    const [showCustom, setShowCustom] = useState(
        !!initial?.instrument && !INSTRUMENTS.includes(initial.instrument),
    );
    const [type, setType] = useState(initial?.type || "Local");
    const [days, setDays] = useState(initial?.days || []);
    const [schedule, setSchedule] = useState(initial?.schedule || {});
    const [errors, setErrors] = useState({});

    // If `initial` arrives later (async fetch), sync it once.
    useEffect(() => {
        if (!initial) return;
        setName(initial.name || "");
        setInstrument(initial.instrument || "");
        setShowCustom(
            !!initial.instrument && !INSTRUMENTS.includes(initial.instrument),
        );
        setCustomInstrument(
            initial.instrument && !INSTRUMENTS.includes(initial.instrument)
                ? initial.instrument
                : "",
        );
        setType(initial.type || "Local");
        setDays(initial.days || []);
        setSchedule(initial.schedule || {});
    }, [initial]);

    // Union of working-days and any existing days (preserve legacy days in edit
    // mode so the user can see / clear them).
    const dayPills = Array.from(
        new Set([...availableDays, ...(initial?.days || [])]),
    ).sort((a, b) => DAYS_OF_WEEK.indexOf(a) - DAYS_OF_WEEK.indexOf(b));

    const toggleDay = (day) => {
        setDays((prev) => {
            if (prev.includes(day)) {
                const next = prev.filter((d) => d !== day);
                setSchedule((s) => {
                    const copy = { ...s };
                    delete copy[day];
                    return copy;
                });
                return next;
            }
            return [...prev, day];
        });
    };

    const setTime = (day, time) =>
        setSchedule((s) => ({ ...s, [day]: time }));

    const submit = (e) => {
        e.preventDefault();
        const errs = {};
        if (!name.trim()) errs.name = "Required";
        if (!instrument.trim()) errs.instrument = "Required";
        if (days.length === 0) errs.days = "Pick at least one lesson day";
        for (const d of days) {
            if (!schedule[d]) {
                errs.schedule = "Assign a time for every selected day";
                break;
            }
        }
        setErrors(errs);
        if (Object.keys(errs).length > 0) {
            toast.error("Please fix the highlighted fields");
            return;
        }
        const sorted = [...days].sort(
            (a, b) => DAYS_OF_WEEK.indexOf(a) - DAYS_OF_WEEK.indexOf(b),
        );
        onSubmit({
            name: name.trim(),
            instrument: instrument.trim(),
            type,
            days: sorted,
            schedule,
        });
    };

    const tid = (s) => `${testIdPrefix}${s}`;

    return (
        <form
            onSubmit={submit}
            className="bg-white rounded-3xl border border-mist card-soft p-6 sm:p-8 space-y-7"
            data-testid={tid("student-form")}
        >
            <div className="grid grid-cols-1 gap-5">
                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                        Student Name
                    </Label>
                    <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Maya Thompson"
                        className="h-12 rounded-xl bg-mist/50 border-mist focus-visible:ring-[#3D6385]"
                        data-testid={tid("input-name")}
                    />
                    {errors.name && (
                        <div className="text-xs font-bold text-danger">
                            {errors.name}
                        </div>
                    )}
                </div>

                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                        Instrument
                    </Label>
                    <div
                        className="flex flex-wrap gap-2"
                        data-testid={tid("instrument-pills")}
                    >
                        {INSTRUMENTS.map((i) => {
                            const active = instrument === i;
                            return (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => {
                                        setInstrument(i);
                                        setShowCustom(false);
                                        setCustomInstrument("");
                                    }}
                                    data-testid={tid(`instrument-${i.toLowerCase()}`)}
                                    aria-pressed={active}
                                    className={`focus-ring h-10 px-4 rounded-full text-sm font-bold border-2 transition-all ${
                                        active
                                            ? "bg-[#3D6385] text-white border-[#3D6385] shadow-md"
                                            : "bg-mist/50 text-slate-600 border-mist hover:bg-mist"
                                    }`}
                                >
                                    {i}
                                </button>
                            );
                        })}
                        <button
                            type="button"
                            onClick={() => {
                                setShowCustom(true);
                                setInstrument(customInstrument);
                            }}
                            data-testid={tid("instrument-other")}
                            aria-pressed={showCustom}
                            className={`focus-ring h-10 px-4 rounded-full text-sm font-bold border-2 transition-all inline-flex items-center gap-1 ${
                                showCustom
                                    ? "bg-ink text-white border-slate-900 shadow-md"
                                    : "bg-mist/50 text-slate-600 border-mist hover:bg-mist"
                            }`}
                        >
                            <Plus size={14} strokeWidth={3} />
                            Other
                        </button>
                    </div>
                    {showCustom && (
                        <Input
                            value={customInstrument}
                            onChange={(e) => {
                                setCustomInstrument(e.target.value);
                                setInstrument(e.target.value);
                            }}
                            placeholder="Type instrument name…"
                            className="h-12 rounded-xl bg-mist/50 border-mist focus-visible:ring-[#3D6385] mt-2"
                            data-testid={tid("input-instrument-custom")}
                            autoFocus
                        />
                    )}
                    {errors.instrument && (
                        <div className="text-xs font-bold text-danger">
                            {errors.instrument}
                        </div>
                    )}
                </div>
            </div>

            <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                    Student Type
                </Label>
                <div className="flex gap-3">
                    {STUDENT_TYPES.map((t) => {
                        const style = STUDENT_TYPE_STYLES[t];
                        return (
                            <button
                                key={t}
                                type="button"
                                onClick={() => setType(t)}
                                data-testid={tid(`type-${t.toLowerCase()}`)}
                                aria-pressed={type === t}
                                className={`focus-ring flex-1 h-12 rounded-xl font-bold text-sm transition-all ${
                                    type === t
                                        ? `${style.solid} shadow-md`
                                        : "bg-mist/50 text-slate-600 hover:bg-mist"
                                }`}
                            >
                                {t}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="space-y-3">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                    Weekly Schedule
                </Label>
                <div className="flex flex-wrap gap-2">
                    {dayPills.map((d) => {
                        const active = days.includes(d);
                        return (
                            <button
                                key={d}
                                type="button"
                                onClick={() => toggleDay(d)}
                                data-testid={tid(`day-pill-${d.toLowerCase()}`)}
                                aria-pressed={active}
                                className={`focus-ring px-4 h-10 rounded-full text-sm font-bold transition-all ${
                                    active
                                        ? "bg-[#3D6385] text-white shadow-md"
                                        : "bg-mist/50 text-slate-600 hover:bg-mist"
                                }`}
                            >
                                {d.slice(0, 3)}
                            </button>
                        );
                    })}
                </div>
                {errors.days && (
                    <div className="text-xs font-bold text-danger">
                        {errors.days}
                    </div>
                )}

                {days.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                        {[...days]
                            .sort(
                                (a, b) =>
                                    DAYS_OF_WEEK.indexOf(a) - DAYS_OF_WEEK.indexOf(b),
                            )
                            .map((d) => (
                                <div
                                    key={d}
                                    className="flex items-center gap-3 p-3 rounded-xl bg-mist/50 border border-mist"
                                >
                                    <span className="font-bold text-sm text-slate-700 w-20 shrink-0">
                                        {d}
                                    </span>
                                    <Input
                                        type="time"
                                        value={schedule[d] || ""}
                                        onChange={(e) => setTime(d, e.target.value)}
                                        className="h-10 rounded-lg bg-white border-mist"
                                        data-testid={tid(`time-${d.toLowerCase()}`)}
                                    />
                                </div>
                            ))}
                    </div>
                )}
                {errors.schedule && (
                    <div className="text-xs font-bold text-danger">
                        {errors.schedule}
                    </div>
                )}
            </div>

            <div className="flex items-center justify-end gap-3 pt-2 border-t border-mist">
                <Link to={cancelTo}>
                    <Button
                        type="button"
                        variant="outline"
                        className="rounded-xl h-12 px-5 font-bold border-mist"
                        data-testid={tid("cancel-btn")}
                    >
                        Cancel
                    </Button>
                </Link>
                <Button
                    type="submit"
                    disabled={submitDisabled}
                    className="rounded-xl bg-[#3D6385] hover:bg-[#345570] text-white font-bold h-12 px-6 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid={tid("save-student-btn")}
                >
                    <Save size={16} strokeWidth={2.8} className="mr-2" />
                    {submitLabel}
                </Button>
            </div>
        </form>
    );
}
